// Shared SQL query-log formatting for the expo-sqlite-async driver.
//
// Both the raw-connection wrapper (`create-performance-logger.ts`, app side)
// and the vendored `ExpoSQLiteAsyncPreparedQuery` (this package) format the
// colored `[sqlite] SELECT table where … 3ms` line with these helpers, so the
// output is identical regardless of which layer emits it.

import ansiStyles from "@-/com.npmjs.ansi-styles";
import { typedArrayLength } from "@-/com.npmjs.typed-array-length";
import { hasLength, hasMinLength, isNonEmpty } from "@-/com.npmjs.ts-array-length";
import arrayLength from "@-/com.npmjs.extra-array__length";
import { yn } from "@-/com.npmjs.yn";

export const queryLogColors = {
  blue: ansiStyles.blue.open,
  cyan: ansiStyles.cyan.open,
  dim: ansiStyles.dim.open,
  green: ansiStyles.green.open,
  red: ansiStyles.red.open,
  // Bold + red-background for the loud slow-scan highlight; stands out from
  // the plain-red used for dim plan details and durations.
  redInverse: `${ansiStyles.bold.open}${ansiStyles.bgRed.open}`,
  reset: ansiStyles.reset.open,
  yellow: ansiStyles.yellow.open,
} as const;

// These four are `g`-flagged (needed for `matchAll` / offset scanning), and a
// global regex carries mutable `lastIndex` state — so each is minted per call
// rather than shared at module scope.
const createTableNameRe = () => /\b(?:FROM|INTO|UPDATE)\s+["`]?(\w+)["`]?/gi;
const createWhereKeywordRe = () => /\bWHERE\b/gi;
/** Keywords that end a WHERE clause at the same nesting level. */
const createClauseTerminatorRe = () => /\b(?:ORDER|LIMIT|GROUP|HAVING|WINDOW|RETURNING)\b/gi;
/**
 * One WHERE condition: a column compared either by `=` to a single operand
 * (placeholder / quoted literal / number / NULL) or by `IN` to a parenthesized
 * list. Indexed matches let each condition's offset map back to the positional
 * `?` parameters it consumes.
 */
const createWhereConditionRe = () =>
  /["`]?(\w+)["`]?\s*(?:=\s*(\?|'(?:[^']|'')*'|-?\d+(?:\.\d+)?|NULL)|\bIN\b\s*\(([^)]*)\))/gi;
const MAX_LOGGED_WHERE_CONDITIONS = 3;
const MAX_LOGGED_IN_VALUES = 4;
const MAX_LOGGED_VALUE_LENGTH = 24;
const EXPLAIN_SCAN_RE = /SCAN(?: TABLE)?/i;
const EXPLAIN_SEARCH_RE = /SEARCH(?: TABLE)?/i;
const EXPLAIN_INDEX_RE = /USING\s+(?:COVERING\s+)?INDEX\s+["`]?([^"`\s]+)["`]?/i;
const EXPLAIN_INTEGER_PRIMARY_KEY_RE = /INTEGER PRIMARY KEY/i;
// `SCAN <name>` names the object being scanned (a real table, or a transient
// subquery result). Capture it so a scan of a CO-ROUTINE/MATERIALIZED subquery
// result can be told apart from a real full-table scan.
const EXPLAIN_SCAN_TARGET_RE = /^\s*SCAN(?: TABLE)?\s+["`]?([^"`\s(]+)/i;
export interface ExplainStats {
  indexUsed: string | null;
  scanType: string | null;
}

export function getQueryType(query: string): string {
  const upperQuery = query.trim().toUpperCase();
  if (upperQuery.startsWith("SELECT")) {
    return "SELECT";
  }
  if (upperQuery.startsWith("INSERT")) {
    return "INSERT";
  }
  if (upperQuery.startsWith("UPDATE")) {
    return "UPDATE";
  }
  if (upperQuery.startsWith("DELETE")) {
    return "DELETE";
  }
  return "QUERY";
}

/**
 * Nesting depth of every character, so a keyword belonging to the top-level
 * statement can be told apart from one inside a subquery. Characters inside
 * string literals / quoted identifiers inherit the enclosing depth and their
 * parentheses are ignored.
 *
 * Drizzle's relational `with:` compiles each relation into a correlated
 * subquery, so a naive "first FROM / first WHERE" scan describes the INNERMOST
 * relation rather than the statement that was actually run — which is how a
 * `user_grapheme_meaning_card` read came to log itself as `SELECT fsrs_card
 * where fsrs_card_id` while EXPLAIN reported the overlay's own index.
 */
function computeNestingDepths(query: string): number[] {
  const depths = Array.from<number>({ length: query.length }).fill(0);
  let depth = 0;
  let quote: string | null = null;
  for (let index = 0; index < query.length; index += 1) {
    const char = query[index];
    depths[index] = depth;
    if (quote !== null) {
      if (char === quote) {
        // A doubled quote is an escaped literal quote, not a terminator.
        if (query[index + 1] === quote) {
          depths[index + 1] = depth;
          index += 1;
        } else {
          quote = null;
        }
      }
      continue;
    }
    if (char === "'" || char === '"' || char === "`") {
      quote = char;
      continue;
    }
    if (char === "(") {
      depth += 1;
    } else if (char === ")") {
      depth = Math.max(0, depth - 1);
      depths[index] = depth;
    }
  }
  return depths;
}

/** First match of `pattern` that starts at top level (outside any parentheses). */
function matchAtTopLevel(
  query: string,
  depths: readonly number[],
  pattern: RegExp,
  fromIndex = 0,
): RegExpMatchArray | null {
  for (const match of query.matchAll(pattern)) {
    if (match.index !== undefined && match.index >= fromIndex && depths[match.index] === 0) {
      return match;
    }
  }
  return null;
}

export function getTableName(query: string): string | null {
  const depths = computeNestingDepths(query);
  return matchAtTopLevel(query, depths, createTableNameRe())?.[1] ?? null;
}

/** Number of positional `?` placeholders appearing before `endIndex` in `query`. */
function countPlaceholdersBefore(query: string, endIndex: number): number {
  let count = 0;
  for (let index = 0; index < endIndex; index += 1) {
    if (query[index] === "?") {
      count += 1;
    }
  }
  return count;
}

/**
 * Render one bound value compactly. Long strings are truncated (a mnemonic body
 * must not swamp the line) and blobs are summarized rather than dumped.
 */
function formatParamValue(value: unknown): string {
  if (value === null || value === undefined) {
    return "null";
  }
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  if (value instanceof Uint8Array) {
    return `<blob ${typedArrayLength(value)}b>`;
  }
  const text = typeof value === "string" ? value : JSON.stringify(value);
  if (typeof text !== "string") {
    return "?";
  }
  return text.length > MAX_LOGGED_VALUE_LENGTH
    ? `${text.slice(0, MAX_LOGGED_VALUE_LENGTH)}…`
    : text;
}

/**
 * The WHERE summary shown in the log line: `grapheme=一`, `grapheme IN (一, 二)`,
 * `fsrs_card_id=abc…`. Bound `?` placeholders are resolved against `params` by
 * counting the placeholders preceding them in the full statement, so the printed
 * value is the one SQLite actually ran with.
 *
 * Values are what make two structurally identical log lines distinguishable —
 * without them a per-tile read of 一 and one of 二 render as the same
 * `where grapheme` line and look like a pointless duplicate. Pass no `params`
 * (or an empty list) and it degrades to the old column-names-only form.
 */
export function getWhereSummary(query: string, params: readonly unknown[] = []): string | null {
  const depths = computeNestingDepths(query);
  const whereMatch = matchAtTopLevel(query, depths, createWhereKeywordRe());
  if (whereMatch?.index === undefined) {
    return null;
  }
  // Absolute offset of the clause, so placeholder counting runs against the
  // whole statement (parameters bound in SELECT/JOIN subqueries come first).
  const clauseStart = whereMatch.index + whereMatch[0].length;
  const terminator = matchAtTopLevel(query, depths, createClauseTerminatorRe(), clauseStart);
  const whereClause = query.slice(clauseStart, terminator?.index ?? query.length);

  const conditions: string[] = [];
  for (const match of whereClause.matchAll(createWhereConditionRe())) {
    if (hasMinLength(conditions, MAX_LOGGED_WHERE_CONDITIONS)) {
      break;
    }
    const [matchText, column, equalityOperand, inList] = match;
    if (column === undefined || match.index === undefined) {
      continue;
    }
    const matchStart = clauseStart + match.index;

    if (equalityOperand !== undefined) {
      if (equalityOperand !== "?") {
        // Inlined literal — already visible in the SQL text.
        conditions.push(`${column}=${equalityOperand}`);
        continue;
      }
      const placeholderIndex = countPlaceholdersBefore(
        query,
        matchStart + matchText.lastIndexOf("?"),
      );
      conditions.push(
        placeholderIndex < arrayLength(params)
          ? `${column}=${formatParamValue(params[placeholderIndex])}`
          : column,
      );
      continue;
    }

    if (inList === undefined) {
      continue;
    }
    const placeholderCount = countPlaceholdersBefore(inList, inList.length);
    if (placeholderCount === 0) {
      conditions.push(`${column} IN (${inList.trim()})`);
      continue;
    }
    const firstPlaceholderIndex = countPlaceholdersBefore(
      query,
      matchStart + matchText.indexOf("?"),
    );
    const values = params
      .slice(firstPlaceholderIndex, firstPlaceholderIndex + placeholderCount)
      .map((value) => formatParamValue(value));
    if (hasLength(values, 0)) {
      conditions.push(column);
      continue;
    }
    const shown = values.slice(0, MAX_LOGGED_IN_VALUES).join(", ");
    const remaining = arrayLength(values) - MAX_LOGGED_IN_VALUES;
    conditions.push(`${column} IN (${shown}${remaining > 0 ? `, +${remaining} more` : ""})`);
  }

  if (hasLength(conditions, 0)) {
    return null;
  }

  return conditions.join(", ");
}

export function getColor(queryType: string): string {
  switch (queryType) {
    case "SELECT": {
      return queryLogColors.green;
    }
    case "INSERT": {
      return queryLogColors.blue;
    }
    case "UPDATE": {
      return queryLogColors.yellow;
    }
    case "DELETE": {
      return queryLogColors.red;
    }
    default: {
      return queryLogColors.dim;
    }
  }
}

export function getDurationColor(duration: number): string {
  if (duration < 5) {
    return queryLogColors.green;
  }
  if (duration < 20) {
    return queryLogColors.yellow;
  }
  return queryLogColors.red;
}

export function parseSqliteExplain(rows: { detail?: unknown }[]): ExplainStats {
  const stats: ExplainStats = {
    indexUsed: null,
    scanType: null,
  };

  const details = rows.map((row) => (typeof row.detail === "string" ? row.detail : ""));
  const fullText = details.join(" ");

  // Collect aliases that name a transient subquery/co-routine result, so a
  // `SCAN <alias>` of one of those isn't mistaken for a real full-table scan.
  const transientSources = new Set<string>();
  // Aliases introduced for a transient subquery/co-routine result. A `SCAN <name>`
  // of one of these is NOT a missing-index full-table scan — it's just reading the
  // already-index-resolved rows of a correlated relation subquery (drizzle's
  // relational `with:` emits one of these per relation), so it must not be flagged.
  for (const match of fullText.matchAll(
    /\b(?:CO-ROUTINE|MATERIALIZE|SUBQUERY)\b\s+["`]?([^"`\s(]+)/gi,
  )) {
    if (match[1] !== undefined) {
      transientSources.add(match[1]);
    }
  }

  // A real full-table scan is a `SCAN` of an object that wasn't materialized as a
  // transient subquery result. Drizzle's relational `with:` emits one
  // `CO-ROUTINE t … SCAN t` per relation even when every base table is reached by
  // index, so flagging those `SCAN t` rows produced noisy false "SLOW SCAN"s.
  const hasRealTableScan = details.some((detail) => {
    if (!EXPLAIN_SCAN_RE.test(detail)) {
      return false;
    }
    const target = detail.match(EXPLAIN_SCAN_TARGET_RE)?.[1];
    return target === undefined || !transientSources.has(target);
  });

  if (hasRealTableScan) {
    stats.scanType = "SCAN (Full Table)";
  } else if (EXPLAIN_SEARCH_RE.test(fullText)) {
    stats.scanType = "SEARCH";
  }

  const indexMatch = fullText.match(EXPLAIN_INDEX_RE);
  if (indexMatch?.[1] !== undefined) {
    stats.indexUsed = indexMatch[1];
  } else if (EXPLAIN_INTEGER_PRIMARY_KEY_RE.test(fullText)) {
    stats.indexUsed = "PRIMARY KEY";
  }

  return stats;
}

export function shouldLogQuery(queryText: string): boolean {
  return queryText.trim() !== "" && !queryText.trim().toUpperCase().startsWith("EXPLAIN");
}

/**
 * Whether to run the per-query `EXPLAIN QUERY PLAN` probe and append the
 * scan/index detail to the log line.
 *
 * Default ON in development so full-table scans are flagged without any opt-in
 * (this is what surfaces a missing index). The probe doubles the round-trips
 * per SELECT, so it stays OFF outside dev unless explicitly enabled. Set
 * `EXPO_PUBLIC_DB_LOG_EXECUTION_TIME=false` to silence it in dev, or `=true` to
 * force it on in a non-dev build.
 */
export function shouldLogExecutionPlan(): boolean {
  if (typeof process === "undefined") {
    return false;
  }
  const flag = yn(process.env.EXPO_PUBLIC_DB_LOG_EXECUTION_TIME);
  if (flag !== undefined) {
    return flag;
  }
  return typeof __DEV__ !== "undefined" && __DEV__;
}

/**
 * Whether the threaded per-callsite logger gets one formatted line per query. React Native
 * production bundles (`__DEV__ === false`) skip it, like the raw-connection logger: on a phone the
 * formatting alone costs about a millisecond per query, several queries run for every study
 * rating, and each line would become a remote info log. Slow and stuck queries are still reported
 * there (`trackQuery`). Runtimes without `__DEV__` (Node scripts and tests) keep their lines, and
 * forcing the execution-plan probe on keeps them in any build.
 */
export function shouldFormatQueryLines(): boolean {
  return typeof __DEV__ === "undefined" || __DEV__ || shouldLogExecutionPlan();
}

/**
 * A full-table `SCAN` is only worth flagging when it was also slow — SQLite
 * deliberately scans tiny tables, so a fast scan is not a problem. Scans at or
 * above this wall-clock threshold (ms) get the loud `⚠ SLOW SCAN` highlight;
 * faster scans stay dim like any other plan detail.
 */
export const SLOW_SCAN_THRESHOLD_MS = 50;

// A SELECT with no WHERE clause is *supposed* to scan — index can't help. Don't
// flag those as SLOW SCAN even if their wall-clock crosses the threshold; the
// scan itself isn't the culprit. Lets `findFirst()` / `findMany()` no-where
// reads stop tripping the warning.
const HAS_WHERE_CLAUSE_RE = /\bWHERE\b/i;

/**
 * Builds the single colored log line for a finished query. `explainStats` is
 * optional `EXPLAIN QUERY PLAN` detail appended in parentheses. `nativeMicros`
 * is the time inside `sqlite3_step` reported by the patched expo-sqlite (see
 * patches/expo-sqlite@56.0.4.patch); when present, rendered after the
 * wall-clock so queue-wait vs. real SQL time is unambiguous. `databaseTag` names
 * the source database (`actorDb` / `contentDb` / `staticDb`) and, when present,
 * REPLACES the generic `[sqlite]` prefix so each line says which DB it hit.
 */
export function formatQueryLine(
  queryText: string,
  duration: number,
  explainStats: ExplainStats | null = null,
  nativeMicros: number | null = null,
  databaseTag: string | null = null,
  params: readonly unknown[] = [],
): string {
  const queryType = getQueryType(queryText);
  const tableName = getTableName(queryText);
  const whereSummary = getWhereSummary(queryText, params);
  const typeColor = getColor(queryType);
  const durationColor = getDurationColor(duration);

  const prefix = databaseTag === null ? "[sqlite]" : `[${databaseTag}]`;
  let line = `${queryLogColors.dim}${prefix}${queryLogColors.reset} ${typeColor}${queryType}${queryLogColors.reset}`;
  if (tableName !== null) {
    line += ` ${tableName}`;
  }
  if (whereSummary !== null) {
    line += ` ${queryLogColors.dim}where ${whereSummary}${queryLogColors.reset}`;
  }

  line += ` ${durationColor}${duration}ms${queryLogColors.reset}`;

  if (nativeMicros !== null) {
    // Format with one decimal of ms precision so 0–10ms SQL doesn't round to 0.
    const nativeMs = (nativeMicros / 1000).toFixed(nativeMicros < 10_000 ? 2 : 0);
    line += ` ${queryLogColors.dim}(native=${nativeMs}ms)${queryLogColors.reset}`;
  }

  if (explainStats !== null) {
    const details: string[] = [];
    const isFullScan = explainStats.scanType?.includes("SCAN") === true;
    // Only a SLOW full scan WITH a WHERE clause is a problem worth shouting
    // about — a no-WHERE SELECT is supposed to scan, SQLite scans tiny tables
    // on purpose, so neither of those is the culprit.
    const hasWhere = HAS_WHERE_CLAUSE_RE.test(queryText);
    const isSlowScan = isFullScan && hasWhere && duration >= SLOW_SCAN_THRESHOLD_MS;

    if (isSlowScan) {
      // Loud, leading highlight so a missing-index scan can't be missed.
      line += ` ${queryLogColors.redInverse} ⚠ SLOW SCAN ${queryLogColors.reset}`;
    }

    if (explainStats.scanType !== null) {
      const scanColor = isFullScan ? queryLogColors.red : queryLogColors.dim;
      details.push(`${scanColor}${explainStats.scanType}${queryLogColors.reset}`);
    }

    if (explainStats.indexUsed !== null) {
      details.push(`idx: ${explainStats.indexUsed}`);
    }

    if (isNonEmpty(details)) {
      line += ` ${queryLogColors.dim}(${details.join(", ")})${queryLogColors.reset}`;
    }
  }

  return line;
}
